import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight, Pencil } from "lucide-react";
import { AppShell } from "../components/layout/AppShell";
import { NeuCard } from "../components/neu/Neu";
import { TILE_3D, tileBtn } from "../features/admin/actionTile";
import { userDetail } from "../api/onboarding";
import { isAdmin } from "../utils/role";
import { PersonalCard } from "../features/admin/PersonalCard";
import { StatusBadge } from "../features/admin/StatusBadge";
import { EmployeeEditPanel } from "../features/admin/EmployeeEditPanel";
import { EmployeeHero } from "../features/admin/EmployeeHero";
import { EmployeeInfoCard } from "../features/admin/EmployeeInfoCard";
import { EmployeePdfButton } from "../features/admin/EmployeePdfButton";
import { EmployeeDataExportButton } from "../features/admin/EmployeeDataExportButton";
import { EmployeeDocsCard } from "../features/admin/EmployeeDocsCard";
import { EmployeeDangerZone } from "../features/admin/EmployeeDangerZone";
import { TransferOwnershipButton } from "../features/admin/TransferOwnershipButton";
import { EmployeeHistory } from "../features/admin/EmployeeHistory";
import { useAuth } from "../context/AuthContext";
import { useDirtyNavigate } from "../context/DirtyGuard";

export default function EmployeeDetailPage() {
  const { t, i18n } = useTranslation();
  const BackArrow = i18n.dir() === "rtl" ? ArrowRight : ArrowLeft;
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editMode = params.get("edit") === "1";
  const { user } = useAuth();
  const requestNav = useDirtyNavigate();
  const [data, setData] = useState(null);
  const [version, setVersion] = useState(0);
  const canEdit = isAdmin(user);

  const load = async () => {
    setData(await userDetail(id));
    setVersion((v) => v + 1);
  };
  useEffect(() => {
    setData(null);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const p = data?.profile;
  const back = editMode ? `/employees/${id}` : "/employees";

  return (
    <AppShell
      title={p?.name || t("detail.title")}
      subtitle={editMode ? t("detail.edit") : t("detail.title")}
      actions={
        <div className="flex w-full items-center justify-between gap-2">
          <button data-testid="detail-back" aria-label={t("detail.back")} title={t("detail.back")} className={`${tileBtn("bg-sky-500")} flex h-11 w-11 items-center justify-center !px-0`} style={TILE_3D} onClick={() => requestNav(back)}>
            <BackArrow className="h-5 w-5" />
          </button>
          <div className="flex flex-wrap items-center gap-2">
            {!editMode ? <EmployeePdfButton userId={id} /> : null}
            {!editMode && canEdit ? <EmployeeDataExportButton userId={id} /> : null}
          </div>
        </div>
      }
    >
      <div className="settings-zoom">
      {!data ? (
        <p className="text-sm text-muted-foreground">{t("app.loading")}</p>
      ) : editMode && canEdit ? (
        <div data-testid="employee-detail-edit"><EmployeeEditPanel employee={{ ...p, id }} onSaved={load} /></div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-3" data-testid="employee-detail">
          <NeuCard className="relative lg:col-span-3" data-testid="employee-profile-card">
            {p?.status ? <StatusBadge status={p.status} className="absolute start-5 top-5" /> : null}
            {canEdit ? (
              <button data-testid="detail-edit-open" aria-label={t("detail.edit")} onClick={() => navigate(`/employees/${id}?edit=1`)}
                className="absolute end-5 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-primary text-white shadow-neu-sm transition hover:scale-105">
                <Pencil className="h-4 w-4" />
              </button>
            ) : null}
            <EmployeeHero profile={p} userId={id} canEdit={canEdit} onDone={load} />
            <div className="my-6 h-px bg-border/50" />
            <EmployeeInfoCard profile={p} branchNames={data.branch_names} />
            <div className="my-6 h-px bg-border/50" />
            <PersonalCard personal={p.personal} userId={id} canEdit={canEdit} onSaved={load} />
          </NeuCard>
          <EmployeeDocsCard userId={id} canEdit={canEdit} />
          {canEdit ? (
            <div className="lg:col-span-3">
              <EmployeeHistory key={version} userId={id} />
            </div>
          ) : null}
          <TransferOwnershipButton targetUser={{ ...p, id }} onDone={load} />
          {canEdit ? <EmployeeDangerZone employee={{ ...p, id }} /> : null}
        </div>
      )}
      </div>
    </AppShell>
  );
}
